import { useState } from "react";
import axios from "axios";
import { MaskLine, Rise } from "@/components/motion/Reveal";
import { OFFICES } from "@/data/site";
import { IDS } from "@/lib/testIds";
import { ArrowUpRight, Check } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/seo/Seo";
import { ldLegalService, ldBreadcrumb } from "@/seo/seoConfig";
import { DIRECT_EMAIL, DIRECT_PHONE, DIRECT_PHONE_TEL } from "@/components/layout/ConsultPopover";
import { Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ full_name: "", email: "", business: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      setError(t.contact.err);
      setStatus("error");
      toast.error(t.contact.err);
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ full_name: "", email: "", business: "", message: "" });
      toast.success(t.contact.toastOk);
    } catch (err) {
      const msg = err?.response?.data?.detail || t.contact.toastFail;
      setError(typeof msg === "string" ? msg : t.contact.err);
      setStatus("error");
      toast.error(t.contact.toastFail);
    }
  };

  return (
    <div className="pt-40">
      <Seo
        title="Contact Chambers · Dubai · RAK · Pune"
        description="Speak with the founding partners of RPLC UAE. Registered office at RAKEZ Business Zone, Ras Al Khaimah; on-ground in Downtown Dubai; India practice in Pune. All inquiries read by a partner within one working day."
        path="/contact"
        keywords={["contact law firm Dubai", "hire lawyer Dubai", "consult lawyer UAE", "cyber lawyer consultation Pune", "book legal consultation Dubai"]}
        jsonLd={[ldLegalService(), ldBreadcrumb([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <Toaster position="bottom-right" theme="dark" richColors />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise><div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow">{t.contact.eyebrow}</span></div></Rise>
        <h1 className="font-serif tracking-tight leading-[0.9] text-[13vw] lg:text-[8vw]">
          <MaskLine delay={0.15}>{t.contact.title1}</MaskLine>
          <MaskLine delay={0.32}><span className="italic-accent">{t.contact.title2}</span></MaskLine>
        </h1>
        <Rise delay={0.5}>
          <p className="mt-10 text-[#A19D94] text-lg max-w-2xl leading-relaxed">{t.contact.lead}</p>
        </Rise>

        <Rise delay={0.6}>
          <div data-testid="contact-direct-hero" className="mt-10 border border-[#222] bg-[#0A0A0A] p-6 lg:p-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2 h-2 bg-[#C84C35]" />
              <span className="chapter-num">Direct</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a href={`mailto:${DIRECT_EMAIL}`} className="flex items-start gap-4 group" data-testid="hero-direct-email">
                <Mail size={20} strokeWidth={1.25} className="text-[#C84C35] mt-1 shrink-0" />
                <div className="min-w-0">
                  <div className="chapter-num text-[#66635C]">Email</div>
                  <div className="font-serif text-2xl text-[#F4F1EB] mt-1 break-all group-hover:text-[#C84C35] transition-colors">{DIRECT_EMAIL}</div>
                </div>
              </a>
              <a href={`tel:${DIRECT_PHONE_TEL}`} className="flex items-start gap-4 group" data-testid="hero-direct-phone">
                <Phone size={20} strokeWidth={1.25} className="text-[#C84C35] mt-1 shrink-0" />
                <div>
                  <div className="chapter-num text-[#66635C]">Phone</div>
                  <div className="font-serif text-2xl text-[#F4F1EB] mt-1 group-hover:text-[#C84C35] transition-colors">{DIRECT_PHONE}</div>
                </div>
              </a>
            </div>
          </div>
        </Rise>
      </section>

      <section className="mt-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <Rise>
              <form data-testid={IDS.contact.form} onSubmit={submit} className="space-y-10">
                <div>
                  <label className="eyebrow">{t.contact.name}</label>
                  <input data-testid={IDS.contact.name} value={form.full_name} onChange={onChange("full_name")} placeholder={t.contact.namePh} className="line-input mt-3" autoComplete="name" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div>
                    <label className="eyebrow">{t.contact.email}</label>
                    <input data-testid={IDS.contact.email} type="email" value={form.email} onChange={onChange("email")} placeholder={t.contact.emailPh} className="line-input mt-3" autoComplete="email" />
                  </div>
                  <div>
                    <label className="eyebrow">{t.contact.business}</label>
                    <input data-testid={IDS.contact.business} value={form.business} onChange={onChange("business")} placeholder={t.contact.businessPh} className="line-input mt-3" />
                  </div>
                </div>
                <div>
                  <label className="eyebrow">{t.contact.message}</label>
                  <textarea data-testid={IDS.contact.message} rows={5} value={form.message} onChange={onChange("message")} placeholder={t.contact.messagePh} className="line-input mt-3 resize-none" />
                </div>

                {status === "error" && error && (
                  <div data-testid={IDS.contact.error} className="text-[#C84C35] chapter-num">{error}</div>
                )}

                <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="text-xs text-[#66635C] max-w-md leading-relaxed">{t.contact.disclaimer}</div>
                  <button type="submit" disabled={status === "loading"} data-testid={IDS.contact.submit} className="btn-primary">
                    {status === "loading" ? t.contact.sending : status === "success" ? (<><Check size={14} /> {t.contact.sent}</>) : (<>{t.contact.submit} <ArrowUpRight size={14} strokeWidth={1.5} /></>)}
                  </button>
                </div>

                {status === "success" && (
                  <div data-testid={IDS.contact.success} className="border-l border-[#C84C35] pl-6 py-2">
                    <div className="chapter-num">{t.contact.okTitle}</div>
                    <div className="font-serif text-2xl mt-2">{t.contact.okBody}</div>
                  </div>
                )}
              </form>
            </Rise>
          </div>

          <div className="lg:col-span-5 lg:pl-16 lg:border-l border-[#141414]">
            <Rise>
              <div className="eyebrow mb-6">{t.contact.chambers}</div>
              <div className="space-y-10">
                {OFFICES.map((o) => (
                  <div key={o.city} className="pb-8 border-b border-[#141414] last:border-0">
                    <div className="chapter-num">{o.role}</div>
                    <div className="font-serif text-4xl mt-2">{o.city}, {o.country}</div>
                    <div className="mt-3 text-[#A19D94] leading-relaxed">{o.address}</div>
                    <div className="chapter-num mt-3 text-[#66635C]">{o.coords}</div>
                  </div>
                ))}
              </div>
              <div className="mt-12">
                <div className="eyebrow mb-3">{t.contact.direct}</div>
                <a href={`mailto:${DIRECT_EMAIL}`} className="link-hover text-[#F4F1EB] block" data-testid="sidebar-direct-email">{DIRECT_EMAIL}</a>
                <a href={`tel:${DIRECT_PHONE_TEL}`} className="link-hover text-[#F4F1EB] block mt-2" data-testid="sidebar-direct-phone">{DIRECT_PHONE}</a>
              </div>
            </Rise>
          </div>
        </div>
      </section>

      <section className="mt-32 border-y border-[#141414] py-10 marquee-mask">
        <div className="marquee-track">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-16 pr-16">
              {["Pune", "Dubai", "Ras Al Khaimah", "Delaware"].map((c, i) => (
                <span key={`${k}-${i}`} className="font-serif italic text-5xl lg:text-7xl text-[#141414] whitespace-nowrap">{c} · </span>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
